import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import {
  AuthSessionService,
  acceptsBearer,
  isAuthorityUrl,
  isOneLoginAccessToken,
  rewriteApiUrl,
} from '../auth';

/**
 * Local-only APIM simulation claims (must match APIM → module header contract).
 * Never enable simulateApimHeaders against a publicly reachable API.
 */
interface OneLoginAccessClaims {
  sub?: string;
  email?: string;
  role?: string;
  firm_id?: string;
  firmId?: string;
  aud?: string | string[];
  iss?: string;
}

interface DevAzureTokenResponse {
  accessToken: string;
  expiresOn: string;
  audience?: string;
}

function isEntraIssuer(iss: string | undefined): boolean {
  if (!iss) {
    return false;
  }
  return (
    iss.includes('sts.windows.net') ||
    iss.includes('login.microsoftonline.com')
  );
}

/**
 * Browser → SP: Authorization = the active sign-in system's access token (core/auth), sent only to
 * that system's SP API hosts and the communication API.
 * - OneLogin: through APIM where the environment has it. APIM validates the JWT, injects X-User-*,
 *   swaps Authorization to Azure AD MI. Do not send UserId / X-User-* from the browser through real APIM.
 * - Identity: straight to the SP host (storeapi.*), as before OneLogin; APIM does not accept Identity tokens.
 *
 * Local without APIM (environment.local): SP API ApimGateway RequireAzureAd=false
 * reads OneLogin Bearer claims itself — still no X-User-* required.
 * Optional simulateApimHeaders / useAzureAdBearer remain for strict local APIM simulation only.
 */
@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  private azureTokenCache: { token: string; expiresAtMs: number } | null = null;
  private azureTokenInflight: Promise<string | null> | null = null;

  constructor(
    private authSession: AuthSessionService,
    private cookieService: CookieService
  ) {}

  intercept(
    original: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (isAuthorityUrl(original.url)) {
      return next.handle(original);
    }

    // Local Azure AD bootstrap — must not recurse through this interceptor.
    if (original.url.includes('/auth/dev-azure-token')) {
      return next.handle(original);
    }

    const provider = this.authSession.provider;
    const url = rewriteApiUrl(original.url, provider);
    const request = url === original.url ? original : original.clone({ url });
    if (!acceptsBearer(url, provider)) {
      return next.handle(request);
    }

    if (provider !== 'onelogin') {
      return this.authSession.accessToken$().pipe(
        switchMap((token) =>
          next
            .handle(
              token
                ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
                : request
            )
            .pipe(tap((event) => this.captureSpIdentityHeaders(event)))
        )
      );
    }

    return this.authSession.accessToken$().pipe(
      switchMap((oneLoginToken) => {
        const headers: Record<string, string> = {};

        // Local-only: simulate APIM claim → X-User-* injection. Off for Dev/APIM.
        if (
          oneLoginToken &&
          environment.simulateApimHeaders &&
          isOneLoginAccessToken(oneLoginToken)
        ) {
          Object.assign(headers, this.buildApimHeadersFromToken(oneLoginToken));
        } else if (
          oneLoginToken &&
          environment.simulateApimHeaders &&
          !isOneLoginAccessToken(oneLoginToken)
        ) {
          console.error(
            '[Auth] Access token is encrypted/legacy (not RS256 JWS). ' +
              'X-User-* headers cannot be built. Sign in via OneLogin after API restart with DisableAccessTokenEncryption.'
          );
        }

        if (!(environment as { useAzureAdBearer?: boolean }).useAzureAdBearer) {
          if (oneLoginToken) {
            headers['Authorization'] = `Bearer ${oneLoginToken}`;
          }
          return next.handle(request.clone({ setHeaders: headers })).pipe(
            tap((event) => this.captureSpIdentityHeaders(event))
          );
        }

        // Local without APIM: Authorization must be Azure AD (SP API RequireAzureAd=true).
        return from(this.resolveAzureAdBearer()).pipe(
          switchMap((azureToken) => {
            if (azureToken) {
              headers['Authorization'] = `Bearer ${azureToken}`;
            } else if (oneLoginToken) {
              console.error(
                '[Auth] Failed to acquire local Azure AD token. ' +
                  'Run: az login --tenant f99d12fe-54d6-4bd5-ba7f-8c849f2ba489 ' +
                  '--scope api://1962552c-1666-4038-a231-2f9e6c866513/access_as_user'
              );
              headers['Authorization'] = `Bearer ${oneLoginToken}`;
            }
            return next.handle(request.clone({ setHeaders: headers })).pipe(
              tap({
                next: (event) => this.captureSpIdentityHeaders(event),
                error: (err) => this.captureSpIdentityHeaders(err),
              })
            );
          }),
          catchError((err) => {
            console.error('[Auth] Azure AD bearer acquisition error', err);
            if (oneLoginToken) {
              headers['Authorization'] = `Bearer ${oneLoginToken}`;
            }
            return next.handle(request.clone({ setHeaders: headers })).pipe(
              tap({
                next: (event) => this.captureSpIdentityHeaders(event),
                error: (e) => this.captureSpIdentityHeaders(e),
              })
            );
          })
        );
      })
    );
  }

  private async resolveAzureAdBearer(): Promise<string | null> {
    const now = Date.now();
    if (this.azureTokenCache && this.azureTokenCache.expiresAtMs > now + 60_000) {
      return this.azureTokenCache.token;
    }

    if (this.azureTokenInflight) {
      return this.azureTokenInflight;
    }

    this.azureTokenInflight = this.fetchDevAzureToken().finally(() => {
      this.azureTokenInflight = null;
    });

    return this.azureTokenInflight;
  }

  private async fetchDevAzureToken(): Promise<string | null> {
    const url = `${environment.apiVersion2Url}auth/dev-azure-token`;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        const body = await res.text();
        console.error('[Auth] dev-azure-token failed', res.status, body);
        return null;
      }
      const data = (await res.json()) as DevAzureTokenResponse;
      if (!data?.accessToken) {
        return null;
      }

      try {
        const claims = jwtDecode<{ iss?: string; exp?: number }>(data.accessToken);
        if (!isEntraIssuer(claims.iss)) {
          console.error('[Auth] dev-azure-token returned non-Entra iss=', claims.iss);
          return null;
        }
        const expMs = claims.exp
          ? claims.exp * 1000
          : Date.parse(data.expiresOn) || Date.now() + 30 * 60_000;
        this.azureTokenCache = { token: data.accessToken, expiresAtMs: expMs };
      } catch {
        this.azureTokenCache = {
          token: data.accessToken,
          expiresAtMs: Date.parse(data.expiresOn) || Date.now() + 30 * 60_000,
        };
      }

      return data.accessToken;
    } catch (e) {
      console.error('[Auth] fetch dev-azure-token error', e);
      return null;
    }
  }

  /** Capture SP response identity headers into cookies (never send them as request headers). */
  private captureSpIdentityHeaders(eventOrError: unknown): void {
    const headers =
      eventOrError instanceof HttpResponse
        ? eventOrError.headers
        : (eventOrError as { headers?: HttpResponse<unknown>['headers'] })?.headers;
    if (!headers) {
      return;
    }

    // Always path=/ so AuthGuard / GlobalComponent reads match (ngx-cookie defaults to current path).
    const set = (name: string, value: string) =>
      this.cookieService.set(name, value, undefined, '/');

    const userId = headers.get('userid');
    if (userId) {
      set('userId', userId);
    }
    const businessId = headers.get('businessid');
    if (businessId) {
      set('businessid', businessId);
    }
    const theme = headers.get('themecolor');
    if (theme) {
      set('themecolor', theme);
    }
    const cb = headers.get('cbcustomerid');
    if (cb) {
      set('customerId', cb);
    }
    const external = headers.get('externaluserguid');
    if (external) {
      set('externalUserGUID', external);
    }

    if (
      this.cookieService.get('customerId') &&
      this.cookieService.get('externalUserGUID')
    ) {
      sessionStorage.setItem('spAuthContextLoadedAt', String(Date.now()));
    }
  }

  private buildApimHeadersFromToken(token: string): Record<string, string> {
    try {
      const claims = jwtDecode<OneLoginAccessClaims>(token);
      const headers: Record<string, string> = {};

      if (claims.sub) {
        headers['X-User-Id'] = claims.sub;
      }
      if (claims.email) {
        headers['X-User-Email'] = claims.email;
      }
      headers['X-User-Roles'] = claims.role ?? '';
      const firmId = claims.firm_id || claims.firmId;
      if (firmId) {
        headers['X-Firm-Id'] = firmId;
      }

      return headers;
    } catch {
      return {};
    }
  }
}
